import { useMemo, useState } from 'react';
import type { FormEvent } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ChevronDown, ChevronRight, Search } from 'lucide-react';
import { KNOWN_UPSTREAM_PROTOCOLS, MAX_SWITCH_REASON_LENGTH } from '@nadoutong/contracts';
import type {
  ChannelRecord,
  EffectiveEgressPath,
  EffectiveTenantRouteRecord,
  PinReconciliationRow,
  RoutingConfigSummaryRecord,
  RoutingSkuTargetRecord,
  UpstreamProtocol,
} from '@nadoutong/contracts';
import { useChannelModelsList } from '../api/useChannelModels.ts';
import { useRoutingBreakers } from '../api/useRoutingBreakers.ts';
import {
  useCloneRoutingConfig,
  useCreateRoutingConfigFromDefault,
  useEffectiveTenantRoute,
  useAssignOrgRoutingConfig,
  useSetRoutingConfigDeprecation,
  useRenameRoutingConfig,
  useRoutingConfigs,
  useRoutingDefaultSkuTargetsMap,
  useSetGlobalRoutingConfig,
} from '../api/useTenantRouting.ts';
import { formatLocalTimestamp } from '../components/formatters.ts';
import { useChannelsList } from '../api/useChannels.ts';
import { OpsApiError } from '../api/client.ts';
import { coerceErrorMessage, useToast } from '../components/Toast.tsx';
import { useT, type I18nKey } from '../i18n/index.ts';
import { GlobalDefaultParityDialog } from './tenant-routing/GlobalDefaultParityDialog.tsx';
import { RoutingLadderNote } from './tenant-routing/RoutingLadderNote.tsx';
import { capabilityStatusLabel } from './capabilityStatus.ts';
import {
  Button,
  Chip,
  ConfirmDialog,
  Dialog,
  Drawer,
  FormField,
  Input,
  PageHeader,
  Select,
  TBody,
  Table,
  TableEmpty,
  Td,
  Textarea,
  Th,
  THead,
  Tr,
} from '../ui/index.ts';
import { RoutingBreakersDrawer } from './RoutingBreakersDrawer.tsx';
import { RoutingLoadDrawer } from './routing-load/RoutingLoadDrawer.tsx';
import { RoutingLoadStrip } from './routing-load/RoutingLoadStrip.tsx';

import { TenantRoutingConfigSheet } from './TenantRoutingConfigSheet.tsx';
import { EmergencyOverridePanel } from './routing-dr/EmergencyOverridePanel.tsx';
import { OverrideInForceNotice } from './routing-dr/OverrideInForceNotice.tsx';
import { RoutingDrPlansPanel } from './routing-dr/RoutingDrPlansPanel.tsx';
import { RoutingSwitchEventsPanel } from './routing-dr/RoutingSwitchEventsPanel.tsx';
import {
  ROUTING_CONVERGENCE_WORST_CASE_SECONDS,
  convergenceOutcome,
  convergenceWindowSeconds,
} from './routing-dr/convergence.ts';
import {
  buildSkuOptions,
  countBlockingBreakers,
  formatSkuTargetIdentity,
  routeConfigStatusLabelKey,
  routeConfigTypeLabelKey,
  isDeprecated,
  type SkuOption,
} from './tenantRoutingShared.ts';

const PROTOCOL_OPTIONS: readonly UpstreamProtocol[] = KNOWN_UPSTREAM_PROTOCOLS;

/**
 * #1919. The deprecation refusal is reachable even with the picker filtered:
 * the config list can be stale by the time the operator submits, and a deep
 * link or a programmatic caller never saw the filter at all. Falling through to
 * `coerceErrorMessage` would put the gateway's English sentence and a raw
 * config id in front of a Chinese-language operator.
 */
function deprecationAwareMessage(
  t: ReturnType<typeof useT>,
  error: unknown,
): string {
  if (error instanceof OpsApiError && error.code === 'ROUTE_CONFIG_DEPRECATED') {
    return t('tenant_routing.deprecation.error.blocked');
  }
  return coerceErrorMessage(error);
}

interface EffectiveDraft {
  org_id: string;
  sku_id: string;
  protocol: UpstreamProtocol;
}

type EffectiveQuery = EffectiveDraft;

interface CreateConfigDraft {
  name: string;
  description: string;
  assign_org_id: string;
}

interface CloneConfigDraft {
  source_config_id: string;
  source_name: string;
  name: string;
  description: string;
  assign_org_id: string;
}

interface AssignConfigDraft {
  config_id: string;
  org_id: string;
}

interface RenameConfigDraft {
  config_id: string;
  name: string;
}

/**
 * #1421: a global switch is recorded in `routing_switch_events` and the
 * gateway requires a stated reason, so the action opens a dialog to collect
 * one instead of firing on click.
 */
interface SetGlobalConfigDraft {
  config_id: string;
  config_name: string;
  reason: string;
}

const EMPTY_EFFECTIVE_DRAFT: EffectiveDraft = {
  org_id: '',
  sku_id: '',
  protocol: 'anthropic',
};

export function TenantRoutingPage(): JSX.Element {
  const t = useT();
  const toast = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const channels = useChannelsList();
  const channelModels = useChannelModelsList();
  const routingConfigs = useRoutingConfigs();
  const createRoutingConfig = useCreateRoutingConfigFromDefault();
  const cloneRoutingConfig = useCloneRoutingConfig();
  const setGlobalRoutingConfig = useSetGlobalRoutingConfig();
  const assignRoutingConfig = useAssignOrgRoutingConfig();
  const setConfigDeprecation = useSetRoutingConfigDeprecation();
  const renameRoutingConfig = useRenameRoutingConfig();

  const [createDraft, setCreateDraft] = useState<CreateConfigDraft | null>(null);
  const [cloneDraft, setCloneDraft] = useState<CloneConfigDraft | null>(null);
  const [assignDraft, setAssignDraft] = useState<AssignConfigDraft | null>(null);
  const [renameDraft, setRenameDraft] = useState<RenameConfigDraft | null>(null);
  const [setGlobalDraft, setSetGlobalDraft] = useState<SetGlobalConfigDraft | null>(null);
  const [isRouteCheckDrawerOpen, setIsRouteCheckDrawerOpen] = useState(false);
  const [isBreakersDrawerOpen, setIsBreakersDrawerOpen] = useState(false);
  const [isLoadDrawerOpen, setIsLoadDrawerOpen] = useState(false);
  const [isParityDialogOpen, setIsParityDialogOpen] = useState(false);
  const [deprecationDraft, setDeprecationDraft] = useState<RoutingConfigSummaryRecord | null>(null);
  const [effectiveDraft, setEffectiveDraft] = useState<EffectiveDraft>(EMPTY_EFFECTIVE_DRAFT);
  const [effectiveParams, setEffectiveParams] = useState<EffectiveQuery | null>(null);

  // Deep link: the detail sheet is driven by `?config=<id>` so the
  // customer-360 surface (PR-4) can jump straight into a config.
  const openConfigId = searchParams.get('config');

  const effective = useEffectiveTenantRoute(
    effectiveParams === null ? null : {
      org_id: effectiveParams.org_id,
      protocol: effectiveParams.protocol,
      sku_id: effectiveParams.sku_id,
    },
  );

  const platformChannels = useMemo(
    () => [...(channels.data ?? [])].sort((a, b) => {
      const vendor = a.vendor.localeCompare(b.vendor);
      return vendor !== 0 ? vendor : a.slug.localeCompare(b.slug);
    }),
    [channels.data],
  );
  const routeConfigRows = useMemo(
    () => routingConfigs.data?.configs ?? [],
    [routingConfigs.data],
  );
  // A config with no marker (or served by a projection that omits the field)
  // belongs in the live library — the marker is an operator hint, so its
  // absence must never hide a config.
  const liveConfigRows = useMemo(
    () => routeConfigRows.filter((row) => !isDeprecated(row)),
    [routeConfigRows],
  );
  const deprecatedConfigRows = useMemo(
    () => routeConfigRows.filter((row) => isDeprecated(row)),
    [routeConfigRows],
  );
  // Deprecated configs pile up and are rarely the one an operator is after,
  // so their group starts collapsed. A deep link to one of them (`?config=`)
  // is the exception: the group opens so the row behind the sheet is visible.
  const [deprecatedExpanded, setDeprecatedExpanded] = useState(false);
  const [deepLinkExpandedFor, setDeepLinkExpandedFor] = useState<string | null>(null);
  if (openConfigId === null && deepLinkExpandedFor !== null) {
    setDeepLinkExpandedFor(null);
  } else if (
    openConfigId !== null
    && openConfigId !== deepLinkExpandedFor
    && deprecatedConfigRows.some((row) => row.id === openConfigId)
  ) {
    // Adjust-during-render rather than an effect: the row must be visible on
    // the same paint the deep link resolves on. Latched per open sheet so a
    // manual collapse while it is open sticks; closing the sheet re-arms it.
    setDeepLinkExpandedFor(openConfigId);
    setDeprecatedExpanded(true);
  }
  const routeConfigAssignments = useMemo(
    () => routingConfigs.data?.assignments ?? [],
    [routingConfigs.data],
  );
  const activeRouteConfigCount = useMemo(
    () => routeConfigRows.filter((config) => config.status === 'active').length,
    [routeConfigRows],
  );
  const assignedRouteConfigCustomerCount = useMemo(
    () => routeConfigRows.reduce((sum, config) => sum + (config.assigned_org_count ?? 0), 0),
    [routeConfigRows],
  );
  // Epic #898 P5: the health strip counts SKUs (namespace/model) with no
  // enabled default pool target. The gateway only exposes per-SKU GETs, so
  // the page fans out one query per catalog SKU; the strip renders a
  // neutral pending state until the catalog + fan-out settle so it never
  // claims "Healthy" off incomplete data.
  const skuOptions = useMemo(
    () => buildSkuOptions(channelModels.data ?? [], platformChannels),
    [channelModels.data, platformChannels],
  );
  const skuIds = useMemo(() => skuOptions.map((sku) => sku.skuId), [skuOptions]);
  const defaultSkuTargets = useRoutingDefaultSkuTargetsMap(skuIds);
  const missingSkuPending = channelModels.isPending || defaultSkuTargets.isPending;
  const poolTargetCount = useMemo(() => {
    let sum = 0;
    for (const targets of defaultSkuTargets.targetsBySku.values()) {
      sum += targets.length;
    }
    return sum;
  }, [defaultSkuTargets.targetsBySku]);
  const missingSkuCount = useMemo(
    () => skuOptions.filter((sku) => {
      const targets = defaultSkuTargets.targetsBySku.get(sku.skuId);
      return targets !== undefined && !targets.some((target) => target.enabled);
    }).length,
    [defaultSkuTargets.targetsBySku, skuOptions],
  );
  const routingBreakers = useRoutingBreakers();
  // Health-strip count: open + forced entries (half-open is recovering).
  const breakerCount = useMemo(
    () => countBlockingBreakers(routingBreakers.data?.breakers ?? []),
    [routingBreakers.data],
  );

  function openConfigSheet(configId: string): void {
    setSearchParams((previous) => {
      const next = new URLSearchParams(previous);
      next.set('config', configId);
      return next;
    });
  }

  function closeConfigSheet(): void {
    setSearchParams((previous) => {
      const next = new URLSearchParams(previous);
      next.delete('config');
      next.delete('sku');
      return next;
    });
  }

  function openCloneDialog(config: RoutingConfigSummaryRecord): void {
    setCloneDraft({
      source_config_id: config.id,
      source_name: config.name,
      name: t('tenant_routing.configs.clone_default_name', { name: config.name }),
      description: config.description ?? '',
      assign_org_id: '',
    });
  }

  /**
   * Announce a completed copy, naming stranded pins when there are any
   * (#1384 D09). One helper for both entry points because they share the copy
   * path and therefore share the failure mode; splitting them would let the
   * two drift into reporting the same fact differently.
   */
  function reportClonedPins(
    pins: readonly PinReconciliationRow[] | undefined,
    successKey: I18nKey = 'tenant_routing.configs.toast.cloned',
  ): void {
    if (pins === undefined || pins.length === 0) {
      toast.success(t(successKey));
      return;
    }
    toast.info(t('tenant_routing.configs.toast.cloned_incompatible_pins', {
      count: pins.length,
    }));
  }

  async function handleCreateConfigSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (createDraft === null) return;
    const name = createDraft.name.trim();
    if (name.length === 0) {
      toast.error(t('tenant_routing.configs.error.name_required'));
      return;
    }
    try {
      const response = await createRoutingConfig.mutateAsync({
        name,
        description: createDraft.description.trim() || undefined,
        assign_org_id: createDraft.assign_org_id.trim() || undefined,
      });
      setCreateDraft(null);
      reportClonedPins(response.incompatible_pins, 'tenant_routing.configs.toast.created');
      openConfigSheet(response.config.id);
    } catch (error) {
      toast.error(coerceErrorMessage(error));
    }
  }

  async function handleCloneConfigSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (cloneDraft === null) return;
    const name = cloneDraft.name.trim();
    if (name.length === 0) {
      toast.error(t('tenant_routing.configs.error.name_required'));
      return;
    }
    try {
      const response = await cloneRoutingConfig.mutateAsync({
        configId: cloneDraft.source_config_id,
        name,
        description: cloneDraft.description.trim() || undefined,
        assign_org_id: cloneDraft.assign_org_id.trim() || undefined,
      });
      setCloneDraft(null);
      // The clone committed either way; what differs is whether every copied
      // pin still resolves (#1384). Reported as its own message rather than a
      // quieter success — the rows were copied verbatim on purpose, so an
      // operator who reads "cloned" and nothing else inherits stranded supply
      // without being told. Same reasoning as the global-switch outcomes above.
      reportClonedPins(response.incompatible_pins);
      openConfigSheet(response.config.id);
    } catch (error) {
      toast.error(coerceErrorMessage(error));
    }
  }

  async function handleRenameConfigSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (renameDraft === null) return;
    const name = renameDraft.name.trim();
    if (name.length === 0) {
      toast.error(t('tenant_routing.configs.error.name_required'));
      return;
    }
    try {
      await renameRoutingConfig.mutateAsync({ configId: renameDraft.config_id, name });
      setRenameDraft(null);
      toast.success(t('tenant_routing.configs.toast.renamed'));
    } catch (error) {
      toast.error(coerceErrorMessage(error));
    }
  }

  async function handleAssignConfigSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (assignDraft === null) return;
    const configId = assignDraft.config_id.trim();
    const orgId = assignDraft.org_id.trim();
    if (configId.length === 0 || orgId.length === 0) {
      toast.error(t('tenant_routing.configs.error.assign_required'));
      return;
    }
    try {
      await assignRoutingConfig.mutateAsync({ configId, orgId });
      setAssignDraft(null);
      toast.success(t('tenant_routing.configs.toast.assigned'));
    } catch (error) {
      toast.error(deprecationAwareMessage(t, error));
    }
  }

  /**
   * The gateway distinguishes several switch rejections (#1421) and each one
   * asks the operator for a different action, so they must not collapse into
   * one generic failure toast.
   *
   * `INVALID_PAYLOAD` is the awkward one: the gateway returns it both for a
   * missing reason *and* for a reason over `MAX_SWITCH_REASON_LENGTH`. Telling
   * someone who pasted a 1,200-character incident summary to "enter a reason"
   * is worse than useless, so the length decides which message applies. The
   * over-length side is only reachable if the gateway's cap is lower than the
   * contract constant this field enforces; the field itself cannot submit one.
   */
  function setGlobalErrorMessage(error: unknown, reason: string): string {
    if (error instanceof OpsApiError) {
      if (error.code === 'ROUTING_PREFLIGHT_TOKEN_STALE') {
        return t('tenant_routing.configs.error.preflight_stale');
      }
      if (error.code === 'ROUTING_SWITCH_HEAD_MOVED') {
        return t('tenant_routing.configs.error.switch_head_moved');
      }
      if (error.code === 'INVALID_PAYLOAD') {
        return reason.length > MAX_SWITCH_REASON_LENGTH
          ? t('tenant_routing.configs.error.reason_too_long')
          : t('tenant_routing.configs.error.reason_required');
      }
    }
    return coerceErrorMessage(error);
  }

  async function handleSetGlobalConfigSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (setGlobalDraft === null) return;
    const reason = setGlobalDraft.reason.trim();
    if (reason.length === 0) {
      // Blocked before the request rather than sent and bounced. Surfaced as a
      // page-level toast, consistent with the other validations in this file.
      toast.error(t('tenant_routing.configs.error.reason_required'));
      return;
    }
    // Defence in depth for programmatic callers only: through the dialog this
    // is unreachable, because the textarea's `maxLength` makes an over-length
    // value constraint-invalid and the form refuses to submit before this
    // handler runs (asserted in tenant-routing-page.test.tsx).
    if (reason.length > MAX_SWITCH_REASON_LENGTH) {
      toast.error(t('tenant_routing.configs.error.reason_too_long'));
      return;
    }
    try {
      const response = await setGlobalRoutingConfig.mutateAsync({
        configId: setGlobalDraft.config_id,
        reason,
      });
      setSetGlobalDraft(null);
      // The switch committed either way; what differs is whether the fleet is
      // already serving it. Both non-converged cases are reported as their own
      // message, not as a quieter success — an operator who reads "done" and
      // then reads traffic inside the propagation window draws the wrong
      // conclusion, and that is just as true when the response carried no
      // convergence at all (`cache` is optional on the wire) as when it
      // reported a partial publish.
      // An engaged override sits above the global config in the ladder, so a
      // global switch that committed and converged perfectly is still not what
      // any tenant is being served. That outranks every convergence outcome
      // below: convergence describes whether the fleet can *see* the write,
      // and this describes whether the write has any effect at all.
      if (response.override_in_force === true) {
        toast.info(t('routing_dr.events.toast.switched_override_in_force'));
        return;
      }
      const outcome = convergenceOutcome(response.cache);
      if (outcome === 'degraded') {
        toast.info(t('routing_dr.events.toast.switched_degraded', {
          // `event.switched_at` and `cache.effective_after` are both gateway
          // clocks, so the window they bracket is skew-free.
          seconds: convergenceWindowSeconds(
            response.cache,
            response.event.switched_at,
            Date.now(),
          ),
        }));
      } else if (outcome === 'unknown') {
        toast.info(t('routing_dr.events.toast.switched_unknown', {
          seconds: ROUTING_CONVERGENCE_WORST_CASE_SECONDS,
        }));
      } else {
        toast.success(t('tenant_routing.configs.toast.global_set'));
      }
    } catch (error) {
      toast.error(setGlobalErrorMessage(error, reason));
    }
  }

  function handleEffectiveSubmit(event: FormEvent): void {
    event.preventDefault();
    const normalized = {
      ...effectiveDraft,
      org_id: effectiveDraft.org_id.trim(),
      sku_id: effectiveDraft.sku_id.trim(),
    };
    if (
      normalized.org_id.length === 0 ||
      normalized.sku_id.length === 0
    ) {
      toast.error(t('tenant_routing.error.effective_required'));
      return;
    }
    setEffectiveParams(normalized);
  }

  return (
    <section className="flex flex-col gap-4" data-testid="tenant-routing-page">
      <PageHeader
        title={t('tenant_routing.title')}
        sub={t('tenant_routing.subtitle')}
        actions={
          <div className="flex items-center gap-2">
            <Button
              onClick={() => setIsRouteCheckDrawerOpen(true)}
              data-testid="tenant-routing-open-route-check"
            >
              <Search aria-hidden="true" className="h-3.5 w-3.5" />
              {t('tenant_routing.action.route_check')}
            </Button>
            <Button
              variant="primary"
              onClick={() => setCreateDraft({
                name: t('tenant_routing.configs.default_new_name'),
                description: '',
                assign_org_id: '',
              })}
              data-testid="tenant-routing-config-create"
            >
              {t('tenant_routing.configs.create')}
            </Button>
          </div>
        }
      />

      {/*
        #1422: the emergency override sits directly under the header, above the
        health strip and the config library, because when it is engaged it is
        the single most important fact on this page — it overrides every route
        the tables below describe. A status column further down would be a
        surface an operator has to remember to check.
      */}
      <EmergencyOverridePanel configs={routeConfigRows} />

      <div className="flex justify-end">
        <Button
          size="sm"
          variant="ghost"
          onClick={() => setIsParityDialogOpen(true)}
          data-testid="tenant-routing-parity-open"
        >
          {t('tenant_routing.parity.open')}
        </Button>
      </div>

      <div className="flex flex-col gap-2">
        <RouteHealthStrip
          configCount={routeConfigRows.length}
          activeConfigCount={activeRouteConfigCount}
          assignedCustomerCount={assignedRouteConfigCustomerCount}
          poolTargetCount={poolTargetCount}
          missingSkuCount={missingSkuCount}
          missingSkuPending={missingSkuPending}
          breakerCount={breakerCount}
          onOpenBreakers={() => setIsBreakersDrawerOpen(true)}
        />
        <RoutingLoadStrip onOpen={() => setIsLoadDrawerOpen(true)} />
      </div>

      {/*
        The "make global default" action lives in this table, and it does not
        move traffic while the override is engaged.
      */}
      <OverrideInForceNotice />

      <RoutingLadderNote />

      <ConfigLibraryTable
        configs={liveConfigRows}
        loading={routingConfigs.isPending}
        onOpen={openConfigSheet}
        onClone={openCloneDialog}
        onToggleDeprecation={setDeprecationDraft}
        deprecationPending={setConfigDeprecation.isPending}
      />

      {/*
        #1919: deprecated configs keep their own section rather than vanishing.
        They are still reachable, still show their binding count, and orgs
        already on them are still routing through them — the marker only stops
        NEW assignments, so hiding them outright would misrepresent what it did.
      */}
      {deprecatedConfigRows.length === 0 ? null : (
        <div className="flex flex-col gap-1.5">
          <button
            type="button"
            onClick={() => setDeprecatedExpanded((expanded) => !expanded)}
            aria-expanded={deprecatedExpanded}
            className="flex w-fit items-center gap-1.5 rounded-md px-1 py-0.5 text-label text-paper-low transition-colors duration-120 hover:text-paper focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-signal/40"
            data-testid="tenant-routing-config-library-deprecated-toggle"
          >
            {deprecatedExpanded
              ? <ChevronDown aria-hidden="true" className="h-4 w-4 shrink-0" />
              : <ChevronRight aria-hidden="true" className="h-4 w-4 shrink-0" />}
            {t('tenant_routing.deprecation.group_toggle', { count: deprecatedConfigRows.length })}
          </button>
          {!deprecatedExpanded ? null : (
            <ConfigLibraryTable
              configs={deprecatedConfigRows}
              loading={false}
              onOpen={openConfigSheet}
              onClone={openCloneDialog}
              onToggleDeprecation={setDeprecationDraft}
              deprecationPending={setConfigDeprecation.isPending}
              testId="tenant-routing-config-library-deprecated"
            />
          )}
        </div>
      )}

      <GlobalDefaultParityDialog open={isParityDialogOpen} onOpenChange={setIsParityDialogOpen} />

      <ConfirmDialog
        open={deprecationDraft !== null}
        onOpenChange={(open) => {
          if (!open && !setConfigDeprecation.isPending) setDeprecationDraft(null);
        }}
        title={t(deprecationDraft !== null && isDeprecated(deprecationDraft)
          ? 'tenant_routing.deprecation.unmark_title'
          : 'tenant_routing.deprecation.mark_title')}
        body={deprecationDraft !== null && isDeprecated(deprecationDraft)
          ? t('tenant_routing.deprecation.unmark_body')
          : t('tenant_routing.deprecation.mark_body', {
              count: String(deprecationDraft?.assigned_org_count ?? 0),
            })}
        confirmLabel={t(deprecationDraft !== null && isDeprecated(deprecationDraft)
          ? 'tenant_routing.deprecation.unmark'
          : 'tenant_routing.deprecation.mark')}
        cancelLabel={t('common.cancel')}
        closeLabel={t('common.close')}
        loading={setConfigDeprecation.isPending}
        onConfirm={() => {
          if (deprecationDraft === null) return;
          const nextDeprecated = !isDeprecated(deprecationDraft);
          setConfigDeprecation.mutate(
            { configId: deprecationDraft.id, deprecated: nextDeprecated },
            {
              onSuccess: () => {
                setDeprecationDraft(null);
                toast.success(t(nextDeprecated
                  ? 'tenant_routing.deprecation.toast_marked'
                  : 'tenant_routing.deprecation.toast_unmarked'));
              },
              onError: (error) => toast.error(coerceErrorMessage(error)),
            },
          );
        }}
        testId="tenant-routing-deprecation-confirm"
      />

      {/*
        #1421 P3b: the failover half of this page. Plans sit above the switch
        log because that is the reading order during an incident — "which
        config do we go to" comes before "what did we already do".
      */}
      <RoutingDrPlansPanel configs={routeConfigRows} />

      <RoutingSwitchEventsPanel />

      <TenantRoutingConfigSheet
        configId={openConfigId}
        onClose={closeConfigSheet}
        channels={platformChannels}
        assignments={routeConfigAssignments}
        onRename={(config) => setRenameDraft({ config_id: config.id, name: config.name })}
        onClone={openCloneDialog}
        onAssign={(configId) => setAssignDraft({ config_id: configId, org_id: '' })}
        onSetGlobal={(configId) => setSetGlobalDraft({
          config_id: configId,
          config_name: routeConfigRows.find((row) => row.id === configId)?.name ?? configId,
          reason: '',
        })}
        onOpenBreakers={() => setIsBreakersDrawerOpen(true)}
        renaming={renameRoutingConfig.isPending}
        assigning={assignRoutingConfig.isPending}
        settingGlobal={setGlobalRoutingConfig.isPending}
      />

      <Dialog
        open={createDraft !== null}
        onOpenChange={(open) => {
          if (!open) setCreateDraft(null);
        }}
        title={t('tenant_routing.configs.create_title')}
        closeLabel={t('common.close')}
        testId="tenant-routing-config-create-dialog"
        footer={
          <>
            <Button variant="ghost" onClick={() => setCreateDraft(null)} disabled={createRoutingConfig.isPending}>
              {t('common.cancel')}
            </Button>
            <Button
              variant="primary"
              type="submit"
              form="tenant-routing-config-create-form"
              loading={createRoutingConfig.isPending}
              data-testid="tenant-routing-config-save"
            >
              {t('tenant_routing.configs.create_from_global_default')}
            </Button>
          </>
        }
      >
        {createDraft === null ? null : (
          <form
            id="tenant-routing-config-create-form"
            className="flex flex-col gap-4"
            onSubmit={handleCreateConfigSubmit}
            data-testid="tenant-routing-config-create-form"
          >
            <FormField label={t('tenant_routing.configs.name')} fieldId="tenant-routing-config-name">
              <Input
                id="tenant-routing-config-name"
                value={createDraft.name}
                onChange={(event) => {
                  const name = event.currentTarget.value;
                  setCreateDraft((draft) => draft === null ? draft : { ...draft, name });
                }}
                data-testid="tenant-routing-config-name"
              />
            </FormField>
            <FormField label={t('tenant_routing.configs.description')} fieldId="tenant-routing-config-description">
              <Textarea
                id="tenant-routing-config-description"
                value={createDraft.description}
                onChange={(event) => {
                  const description = event.currentTarget.value;
                  setCreateDraft((draft) => draft === null ? draft : { ...draft, description });
                }}
                data-testid="tenant-routing-config-description"
              />
            </FormField>
            <FormField label={t('tenant_routing.configs.assign_org_optional')} fieldId="tenant-routing-config-assign-org">
              <Input
                id="tenant-routing-config-assign-org"
                mono
                value={createDraft.assign_org_id}
                onChange={(event) => {
                  const assignOrgId = event.currentTarget.value;
                  setCreateDraft((draft) => draft === null ? draft : { ...draft, assign_org_id: assignOrgId });
                }}
                placeholder="org_..."
                data-testid="tenant-routing-config-assign-org"
              />
            </FormField>
          </form>
        )}
      </Dialog>

      <Dialog
        open={cloneDraft !== null}
        onOpenChange={(open) => {
          if (!open) setCloneDraft(null);
        }}
        title={t('tenant_routing.configs.clone_title')}
        description={cloneDraft === null ? undefined : (
          <span>
            {t('tenant_routing.configs.clone_source')}
            {': '}
            <span className="text-paper">{cloneDraft.source_name}</span>
          </span>
        )}
        closeLabel={t('common.close')}
        testId="tenant-routing-config-clone-dialog"
        footer={
          <>
            <Button variant="ghost" onClick={() => setCloneDraft(null)} disabled={cloneRoutingConfig.isPending}>
              {t('common.cancel')}
            </Button>
            <Button
              variant="primary"
              type="submit"
              form="tenant-routing-config-clone-form"
              loading={cloneRoutingConfig.isPending}
              data-testid="tenant-routing-config-clone-save"
            >
              {t('tenant_routing.configs.clone_save')}
            </Button>
          </>
        }
      >
        {cloneDraft === null ? null : (
          <form
            id="tenant-routing-config-clone-form"
            className="flex flex-col gap-4"
            onSubmit={handleCloneConfigSubmit}
            data-testid="tenant-routing-config-clone-form"
          >
            <FormField label={t('tenant_routing.configs.name')} fieldId="tenant-routing-config-clone-name">
              <Input
                id="tenant-routing-config-clone-name"
                value={cloneDraft.name}
                onChange={(event) => {
                  const name = event.currentTarget.value;
                  setCloneDraft((draft) => draft === null ? draft : { ...draft, name });
                }}
                data-testid="tenant-routing-config-clone-name"
              />
            </FormField>
            <FormField label={t('tenant_routing.configs.description')} fieldId="tenant-routing-config-clone-description">
              <Textarea
                id="tenant-routing-config-clone-description"
                value={cloneDraft.description}
                onChange={(event) => {
                  const description = event.currentTarget.value;
                  setCloneDraft((draft) => draft === null ? draft : { ...draft, description });
                }}
                data-testid="tenant-routing-config-clone-description"
              />
            </FormField>
            <FormField label={t('tenant_routing.configs.assign_org_optional')} fieldId="tenant-routing-config-clone-assign-org">
              <Input
                id="tenant-routing-config-clone-assign-org"
                mono
                value={cloneDraft.assign_org_id}
                onChange={(event) => {
                  const assignOrgId = event.currentTarget.value;
                  setCloneDraft((draft) => draft === null ? draft : { ...draft, assign_org_id: assignOrgId });
                }}
                placeholder="org_..."
                data-testid="tenant-routing-config-clone-assign-org"
              />
            </FormField>
          </form>
        )}
      </Dialog>

      <Dialog
        open={renameDraft !== null}
        onOpenChange={(open) => {
          if (!open) setRenameDraft(null);
        }}
        title={t('tenant_routing.configs.rename_title')}
        closeLabel={t('common.close')}
        testId="tenant-routing-config-rename-dialog"
        footer={
          <>
            <Button variant="ghost" onClick={() => setRenameDraft(null)} disabled={renameRoutingConfig.isPending}>
              {t('common.cancel')}
            </Button>
            <Button
              variant="primary"
              type="submit"
              form="tenant-routing-config-rename-form"
              loading={renameRoutingConfig.isPending}
              data-testid="tenant-routing-config-rename-save"
            >
              {t('tenant_routing.configs.rename_save')}
            </Button>
          </>
        }
      >
        {renameDraft === null ? null : (
          <form
            id="tenant-routing-config-rename-form"
            className="flex flex-col gap-4"
            onSubmit={handleRenameConfigSubmit}
            data-testid="tenant-routing-config-rename-form"
          >
            <FormField label={t('tenant_routing.configs.name')} fieldId="tenant-routing-config-rename-name">
              <Input
                id="tenant-routing-config-rename-name"
                value={renameDraft.name}
                onChange={(event) => {
                  const name = event.currentTarget.value;
                  setRenameDraft((draft) => draft === null ? draft : { ...draft, name });
                }}
                data-testid="tenant-routing-config-rename-name"
              />
            </FormField>
          </form>
        )}
      </Dialog>

      {/*
        #1421: the global switch is the operator's blast-radius lever, and the
        reason typed here is what `routing_switch_events` carries into the next
        incident review. Same dialog chrome as rename/assign — this is a
        confirming action on a dense operational surface, not a ceremony.
      */}
      <Dialog
        open={setGlobalDraft !== null}
        onOpenChange={(open) => {
          if (!open) setSetGlobalDraft(null);
        }}
        title={t('tenant_routing.configs.make_global_title')}
        closeLabel={t('common.close')}
        testId="tenant-routing-config-make-global-dialog"
        footer={
          <>
            <Button
              variant="ghost"
              onClick={() => setSetGlobalDraft(null)}
              disabled={setGlobalRoutingConfig.isPending}
            >
              {t('common.cancel')}
            </Button>
            <Button
              variant="primary"
              type="submit"
              form="tenant-routing-config-make-global-form"
              loading={setGlobalRoutingConfig.isPending}
              data-testid="tenant-routing-config-make-global-save"
            >
              {t('tenant_routing.configs.make_global_save')}
            </Button>
          </>
        }
      >
        {setGlobalDraft === null ? null : (
          <form
            id="tenant-routing-config-make-global-form"
            className="flex flex-col gap-4"
            onSubmit={handleSetGlobalConfigSubmit}
            data-testid="tenant-routing-config-make-global-form"
          >
            <div className="flex items-center justify-between gap-3 rounded-md bg-well px-3 py-2 text-dense text-paper-low">
              <span>{t('tenant_routing.configs.make_global_target')}</span>
              <span className="font-mono text-paper-mid">{setGlobalDraft.config_name}</span>
            </div>
            <FormField
              label={t('tenant_routing.configs.switch_reason')}
              fieldId="tenant-routing-config-make-global-reason"
              required
              hint={t('tenant_routing.configs.switch_reason_hint')}
            >
              <Textarea
                id="tenant-routing-config-make-global-reason"
                rows={3}
                // The gateway's own limit, imported from the contract so the
                // field cannot silently truncate a reason the gateway would
                // have accepted.
                maxLength={MAX_SWITCH_REASON_LENGTH}
                value={setGlobalDraft.reason}
                onChange={(event) => {
                  const reason = event.currentTarget.value;
                  setSetGlobalDraft((draft) => draft === null ? draft : { ...draft, reason });
                }}
                placeholder={t('tenant_routing.configs.switch_reason_placeholder')}
                data-testid="tenant-routing-config-make-global-reason"
              />
            </FormField>
            <p
              className="m-0 text-dense leading-5 text-paper-low"
              data-testid="tenant-routing-config-make-global-propagation"
            >
              {t('routing_dr.propagation.hint', {
                seconds: ROUTING_CONVERGENCE_WORST_CASE_SECONDS,
              })}
            </p>
          </form>
        )}
      </Dialog>

      <Dialog
        open={assignDraft !== null}
        onOpenChange={(open) => {
          if (!open) setAssignDraft(null);
        }}
        title={t('tenant_routing.configs.assign_title')}
        closeLabel={t('common.close')}
        testId="tenant-routing-config-assign-dialog"
        footer={
          <>
            <Button variant="ghost" onClick={() => setAssignDraft(null)} disabled={assignRoutingConfig.isPending}>
              {t('common.cancel')}
            </Button>
            <Button
              variant="primary"
              type="submit"
              form="tenant-routing-config-assign-form"
              loading={assignRoutingConfig.isPending}
              data-testid="tenant-routing-config-assign-save"
            >
              {t('tenant_routing.configs.assign_save')}
            </Button>
          </>
        }
      >
        {assignDraft === null ? null : (
          <form
            id="tenant-routing-config-assign-form"
            className="flex flex-col gap-4"
            onSubmit={handleAssignConfigSubmit}
            data-testid="tenant-routing-config-assign-form"
          >
            <FormField label={t('tenant_routing.configs.selected_config')} fieldId="tenant-routing-config-assign-config">
              <Select
                value={assignDraft.config_id.length > 0 ? assignDraft.config_id : undefined}
                onValueChange={(configId) => {
                  setAssignDraft((draft) => draft === null ? draft : { ...draft, config_id: configId });
                }}
                options={routeConfigRows
                  .filter((config) => config.status === 'active')
                  .map((config) => ({ value: config.id, label: config.name }))}
                testId="tenant-routing-config-assign-config"
              />
            </FormField>
            <FormField label={t('tenant_routing.configs.assign_org')} fieldId="tenant-routing-config-assign-org-input">
              <Input
                id="tenant-routing-config-assign-org-input"
                mono
                value={assignDraft.org_id}
                onChange={(event) => {
                  const orgId = event.currentTarget.value;
                  setAssignDraft((draft) => draft === null ? draft : { ...draft, org_id: orgId });
                }}
                placeholder="org_..."
                data-testid="tenant-routing-config-assign-org-input"
              />
            </FormField>
            <p
              className="m-0 text-dense text-paper-low"
              data-testid="tenant-routing-config-assign-replaces-stack"
            >
              {t('tenant_routing.configs.assign_replaces_stack')}
            </p>
          </form>
        )}
      </Dialog>

      <RoutingBreakersDrawer
        open={isBreakersDrawerOpen}
        onClose={() => setIsBreakersDrawerOpen(false)}
      />

      <RoutingLoadDrawer
        open={isLoadDrawerOpen}
        onClose={() => setIsLoadDrawerOpen(false)}
      />

      <RouteCheckDrawer
        open={isRouteCheckDrawerOpen}
        onClose={() => setIsRouteCheckDrawerOpen(false)}
        draft={effectiveDraft}
        setDraft={setEffectiveDraft}
        onSubmit={handleEffectiveSubmit}
        skuOptions={skuOptions}
        route={effective.data?.route ?? null}
        egressPath={effective.data?.egress_path}
        loading={effective.isPending && effectiveParams !== null}
      />
    </section>
  );
}

function RouteHealthStrip({
  configCount,
  activeConfigCount,
  assignedCustomerCount,
  poolTargetCount,
  missingSkuCount,
  missingSkuPending,
  breakerCount,
  onOpenBreakers,
}: {
  readonly configCount: number;
  readonly activeConfigCount: number;
  readonly assignedCustomerCount: number;
  readonly poolTargetCount: number;
  readonly missingSkuCount: number;
  /** SKU catalog + default-pool fan-out still resolving — hold the verdict. */
  readonly missingSkuPending: boolean;
  readonly breakerCount: number;
  readonly onOpenBreakers: () => void;
}): JSX.Element {
  const t = useT();
  const healthy = !missingSkuPending && missingSkuCount === 0 && breakerCount === 0;
  const items: readonly {
    readonly label: string;
    readonly value: string;
    readonly tone: 'muted' | 'primary' | 'warning';
    readonly testId?: string;
  }[] = [
    { label: t('tenant_routing.configs.title'), value: String(configCount), tone: 'muted' },
    { label: t('tenant_routing.configs.status.active'), value: String(activeConfigCount), tone: 'muted' },
    { label: t('tenant_routing.configs.users'), value: String(assignedCustomerCount), tone: 'muted' },
    { label: t('tenant_routing.configs.pool_targets'), value: String(poolTargetCount), tone: 'muted' },
    {
      label: t('tenant_routing.sku_pools.missing_routes'),
      value: missingSkuPending ? '…' : String(missingSkuCount),
      tone: missingSkuPending ? 'muted' : missingSkuCount === 0 ? 'primary' : 'warning',
      testId: 'tenant-routing-missing-sku-count',
    },
  ];

  return (
    <div
      className="flex min-h-11 flex-wrap items-center gap-x-4 gap-y-2 rounded-md bg-well px-3 py-2 text-dense"
      data-testid="tenant-routing-health-strip"
    >
      <div className="flex items-center gap-2 pr-2">
        <span
          className={[
            'size-1.5 rounded-full',
            missingSkuPending ? 'bg-paper-faint' : healthy ? 'bg-signal' : 'bg-warn',
          ].join(' ')}
          aria-hidden="true"
        />
        <span
          className="text-micro font-bold text-paper"
          data-testid="tenant-routing-health-verdict"
        >
          {missingSkuPending
            ? t('tenant_routing.status.checking')
            : healthy
              ? t('tenant_routing.status.healthy')
              : t('tenant_routing.status.attention')}
        </span>
      </div>
      {items.map((item) => (
        <div key={item.label} className="flex items-baseline gap-1.5 border-l border-line-soft/20 pl-4">
          <span className="text-micro font-bold text-paper-low">{item.label}</span>
          <span
            className={[
              'font-mono text-body font-medium',
              item.tone === 'warning' ? 'text-warn' : item.tone === 'primary' ? 'text-ok' : 'text-paper',
            ].join(' ')}
            data-testid={item.testId}
          >
            {item.value}
          </span>
        </div>
      ))}
      <button
        type="button"
        onClick={onOpenBreakers}
        className="flex items-baseline gap-1.5 border-l border-line-soft/20 pl-4 transition-colors duration-120 hover:underline"
        data-testid="tenant-routing-breaker-strip"
      >
        <span className="text-micro font-bold text-paper-low">
          {t('tenant_routing.breaker.strip_label')}
        </span>
        <span
          className={[
            'font-mono text-body font-medium',
            breakerCount > 0 ? 'text-bad' : 'text-paper',
          ].join(' ')}
          data-testid="tenant-routing-breaker-count"
        >
          {breakerCount}
        </span>
      </button>
    </div>
  );
}

function ConfigLibraryTable({
  configs,
  loading,
  onOpen,
  onClone,
  onToggleDeprecation,
  deprecationPending,
  caption,
  testId,
}: {
  readonly configs: readonly RoutingConfigSummaryRecord[];
  readonly loading: boolean;
  readonly onOpen: (configId: string) => void;
  readonly onClone: (config: RoutingConfigSummaryRecord) => void;
  readonly onToggleDeprecation: (config: RoutingConfigSummaryRecord) => void;
  readonly deprecationPending: boolean;
  readonly caption?: string;
  readonly testId?: string;
}): JSX.Element {
  const t = useT();
  return (
    <section data-testid={testId ?? 'tenant-routing-config-library'}>
      {caption === undefined ? null : (
        <div className="mb-1.5 text-label text-paper-low">{caption}</div>
      )}
      <Table>
        <THead>
          <Tr interactive={false}>
            <Th>{t('tenant_routing.configs.name')}</Th>
            <Th>{t('tenant_routing.configs.type')}</Th>
            <Th>{t('tenant_routing.table.status')}</Th>
            <Th>{t('tenant_routing.configs.coverage')}</Th>
            <Th num>{t('tenant_routing.configs.users')}</Th>
            <Th>{t('tenant_routing.table.updated')}</Th>
            <Th className="text-right">{t('tenant_routing.table.actions')}</Th>
          </Tr>
        </THead>
        <TBody>
          {loading ? (
            <TableEmpty colSpan={7}>{t('tenant_routing.loading')}</TableEmpty>
          ) : configs.length === 0 ? (
            <TableEmpty colSpan={7} testId="tenant-routing-config-library-empty">
              {t('tenant_routing.configs.empty')}
            </TableEmpty>
          ) : (
            configs.map((config) => (
              <Tr
                key={config.id}
                className="cursor-pointer"
                onClick={() => onOpen(config.id)}
                data-testid={`tenant-routing-config-row-${config.id}`}
              >
                <Td emphasis>
                  <span className="flex items-center gap-2">
                    <span className="truncate">{config.name}</span>
                    {config.is_global_default ? (
                      <Chip tone="signal" lamp={false}>{t('tenant_routing.configs.global_default_badge')}</Chip>
                    ) : null}
                    {!isDeprecated(config) ? null : (
                      <Chip tone="neutral" lamp={false}>{t('tenant_routing.deprecation.badge')}</Chip>
                    )}
                  </span>
                  <span className="mt-0.5 block truncate font-mono text-micro text-paper-low">{config.id}</span>
                </Td>
                <Td>{t(routeConfigTypeLabelKey(config.config_type))}</Td>
                <Td>
                  <Chip tone={config.status === 'active' ? 'ok' : 'neutral'}>
                    {t(routeConfigStatusLabelKey(config.status))}
                  </Chip>
                </Td>
                <Td>
                  {/*
                    #1916: exclusive vs inherit decides what happens to a SKU
                    this config does not cover — fail closed, or fall through to
                    the global default. It is the single most consequential
                    property of a config and was previously only visible after
                    opening the sheet.

                    Exclusive is `neutral` even though it is the mode that can
                    404 a tenant on a SKU nobody remembered to add: nearly every
                    config is exclusive, so colouring it `warn` paints the whole
                    column amber, says nothing, and cheapens `warn` everywhere
                    else on the page. `signal` marks inherit — the rarer mode,
                    and the one whose behaviour surprises people.
                  */}
                  <Chip tone={config.coverage_mode === 'inherit' ? 'signal' : 'neutral'} lamp={false}>
                    {t(config.coverage_mode === 'inherit'
                      ? 'tenant_routing.coverage.short_inherit'
                      : 'tenant_routing.coverage.short_exclusive')}
                  </Chip>
                </Td>
                <Td num data-testid={`tenant-routing-config-users-${config.id}`}>
                  {config.assigned_org_count}
                </Td>
                <Td mono className="whitespace-nowrap">{formatLocalTimestamp(config.updated_at)}</Td>
                <Td className="text-right">
                  <span className="inline-flex items-center gap-1.5">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={(event) => {
                        event.stopPropagation();
                        onOpen(config.id);
                      }}
                      data-testid={`tenant-routing-config-select-${config.id}`}
                    >
                      {t('tenant_routing.library.open')}
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={(event) => {
                        event.stopPropagation();
                        onClone(config);
                      }}
                      data-testid={`tenant-routing-config-clone-row-${config.id}`}
                    >
                      {t('tenant_routing.configs.clone')}
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      disabled={deprecationPending}
                      onClick={(event) => {
                        event.stopPropagation();
                        onToggleDeprecation(config);
                      }}
                      data-testid={`tenant-routing-config-deprecation-${config.id}`}
                    >
                      {isDeprecated(config)
                        ? t('tenant_routing.deprecation.unmark')
                        : t('tenant_routing.deprecation.mark')}
                    </Button>
                  </span>
                </Td>
              </Tr>
            ))
          )}
        </TBody>
      </Table>
    </section>
  );
}

function RouteCheckDrawer({
  open,
  onClose,
  draft,
  setDraft,
  onSubmit,
  skuOptions,
  route,
  egressPath,
  loading,
}: {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly draft: EffectiveDraft;
  readonly setDraft: (updater: (draft: EffectiveDraft) => EffectiveDraft) => void;
  readonly onSubmit: (event: FormEvent) => void;
  readonly skuOptions: readonly SkuOption[];
  readonly route: EffectiveTenantRouteRecord | null;
  readonly egressPath?: EffectiveEgressPath;
  readonly loading: boolean;
}): JSX.Element {
  const t = useT();
  return (
    <Drawer
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      width="lg"
      title={t('tenant_routing.effective.title')}
      description={t('tenant_routing.effective.body')}
      closeLabel={t('common.close')}
      testId="tenant-routing-route-check-drawer"
    >
      <form onSubmit={onSubmit} className="flex flex-col gap-4" data-testid="tenant-routing-effective-form">
        <FormField label={t('tenant_routing.override.org')} fieldId="tenant-routing-effective-org">
          <Input
            id="tenant-routing-effective-org"
            mono
            value={draft.org_id}
            onChange={(event) => {
              const orgId = event.currentTarget.value;
              setDraft((d) => ({ ...d, org_id: orgId }));
            }}
            placeholder="org_..."
            data-testid="tenant-routing-effective-org"
          />
        </FormField>
        <FormField label={t('tenant_routing.field.sku')} fieldId="tenant-routing-effective-sku">
          <Select
            value={draft.sku_id.length > 0 ? draft.sku_id : undefined}
            onValueChange={(skuId) => setDraft((d) => ({ ...d, sku_id: skuId }))}
            options={skuOptions.map((sku) => ({
              value: sku.skuId,
              label: `${sku.skuName} · ${sku.skuId}`,
            }))}
            mono
            testId="tenant-routing-effective-sku"
          />
        </FormField>
        <FormField label={t('tenant_routing.field.protocol')} fieldId="tenant-routing-effective-protocol">
          <Select
            value={draft.protocol}
            onValueChange={(value) => {
              const protocol = PROTOCOL_OPTIONS.find((candidate) => candidate === value);
              if (protocol !== undefined) {
                setDraft((d) => ({ ...d, protocol }));
              }
            }}
            options={PROTOCOL_OPTIONS.map((protocol) => ({ value: protocol, label: protocol }))}
            mono
            testId="tenant-routing-effective-protocol"
          />
        </FormField>
        <Button type="submit" variant="primary" data-testid="tenant-routing-effective-check">
          {t('tenant_routing.effective.check')}
        </Button>
        <p className="m-0 text-dense leading-5 text-paper-low">{t('tenant_routing.effective.sku_chain_hint')}</p>
        <EffectiveResult route={route} egressPath={egressPath} loading={loading} />
      </form>
    </Drawer>
  );
}

function EffectiveResult({
  route,
  egressPath,
  loading,
}: {
  readonly route: EffectiveTenantRouteRecord | null;
  readonly egressPath?: EffectiveEgressPath;
  readonly loading: boolean;
}): JSX.Element | null {
  const t = useT();
  if (loading) {
    return <p className="m-0 mt-2 text-body text-paper-low">{t('tenant_routing.loading')}</p>;
  }
  if (route === null) return null;
  const routeConfig = route.route_config ?? null;
  return (
    <div className="mt-2 rounded-md bg-raised p-4" data-testid="tenant-routing-effective-result">
      <p className="m-0 text-micro font-bold text-paper-low">
        {t(sourceLabelKey(route.source))}
      </p>
      {routeConfig !== null ? (
        <p
          className="m-0 mt-1 break-all text-dense text-paper-low"
          data-testid="tenant-routing-effective-route-config"
        >
          {t('tenant_routing.effective.route_config_ref', {
            name: routeConfig.name ?? routeConfig.id,
            id: routeConfig.id,
          })}
        </p>
      ) : null}
      <p className="m-0 mt-2 break-all text-body font-medium text-paper">
        {route.sku_target != null
          ? formatSkuRouteTarget(route.sku_target)
          : t('tenant_routing.effective.no_sku_target')}
      </p>
      {/*
        `gap` is a contract member the console never rendered, so
        `uncovered_by_emergency_override` — which the DR runbook explicitly
        tells the responder to read, because it means that SKU is failing for
        every tenant right now — could not reach a screen.
      */}
      {route.gap != null ? (
        <p
          role="alert"
          className="m-0 mt-2 rounded border border-bad-line bg-bad-dim/50 px-2.5 py-1.5 text-dense leading-5 text-bad-bright"
          data-testid="tenant-routing-effective-gap"
        >
          {t(route.gap.reason === 'uncovered_by_emergency_override'
            ? 'tenant_routing.effective.gap_uncovered_by_emergency_override'
            : 'tenant_routing.effective.gap_uncovered_by_assigned_config', {
            sku: route.gap.sku_id,
            config: route.gap.config.name ?? route.gap.config.id,
          })}
        </p>
      ) : null}
      {route.sku_target?.capability_id != null ? (
        <p className="m-0 mt-1 break-all font-mono text-micro text-paper-low" data-testid="tenant-routing-effective-capability">
          {route.sku_target.upstream_model_name ?? '—'} · {route.sku_target.capability_id} · {route.sku_target.capability_protocol ?? '—'} · {capabilityStatusLabel(route.sku_target.capability_status, t)}
          {route.sku_target.capability_last_passed_at != null ? ` · ${formatLocalTimestamp(route.sku_target.capability_last_passed_at, { seconds: true })}` : ''}
        </p>
      ) : null}
      {egressPath !== undefined ? (
        <p className="m-0 mt-2 text-dense text-paper-mid" data-testid="tenant-routing-effective-egress-path">
          {egressPathLabel(egressPath, t)}
        </p>
      ) : null}
    </div>
  );
}

function egressPathLabel(
  path: EffectiveEgressPath,
  t: (key: I18nKey, params?: Record<string, string>) => string,
): string {
  const dest = path.dest_region ?? '—';
  const gateway = path.gateway_region;
  if (path.reason === 'unknown_upstream') {
    return t('tenant_routing.effective.egress_path.unknown');
  }
  if (path.reason === 'no_scores') {
    return t('tenant_routing.effective.egress_path.no_scores');
  }
  if (path.reason === 'pinned') {
    return t('tenant_routing.effective.egress_path.pinned', { path: path.path_id, dest });
  }
  if (path.kind === 'via_exit') {
    return t('tenant_routing.effective.egress_path.via', { path: path.path_id, dest, gateway });
  }
  return t('tenant_routing.effective.egress_path.direct', { gateway, dest });
}

/** SKU-partition preview hit (epic #898 P5): `sku_target` replaces `target`. */
function formatSkuRouteTarget(target: RoutingSkuTargetRecord): string {
  return `${target.sku_name} → ${formatSkuTargetIdentity(target)} · ${target.upstream_channel_slug}`;
}

/**
 * No `default:` branch, deliberately (#1422).
 *
 * This switch used to end in `default: return 'tenant_routing.source.none'`,
 * so when `RouteResolutionSource` gained `emergency_override` the compiler
 * stayed silent and the drawer printed **"No route"** directly above the DR
 * config's own target — the first surface an operator reaches for after
 * engaging, telling them it failed while displaying the proof it worked.
 *
 * Exhaustive now: the `never` assignment below makes the next member added to
 * the union a type error here instead of a silent mislabel.
 */
function sourceLabelKey(source: EffectiveTenantRouteRecord['source']):
  | 'tenant_routing.source.emergency_override'
  | 'tenant_routing.source.route_config'
  | 'tenant_routing.source.org_sku'
  | 'tenant_routing.source.org_override'
  | 'tenant_routing.source.sku_default'
  | 'tenant_routing.source.global_default'
  | 'tenant_routing.source.legacy_fallback'
  | 'tenant_routing.source.none' {
  switch (source) {
    case 'emergency_override':
      return 'tenant_routing.source.emergency_override';
    case 'route_config':
      return 'tenant_routing.source.route_config';
    case 'org_sku':
      return 'tenant_routing.source.org_sku';
    case 'org_override':
      return 'tenant_routing.source.org_override';
    case 'sku_default':
      return 'tenant_routing.source.sku_default';
    case 'global_default':
      return 'tenant_routing.source.global_default';
    case 'legacy_fallback':
      return 'tenant_routing.source.legacy_fallback';
    case 'none':
      return 'tenant_routing.source.none';
  }
  const unhandled: never = source;
  throw new Error(`unhandled effective-route source: ${String(unhandled)}`);
}
